import { createApp } from "vue"
import { createPinia } from 'pinia'
import axios from "axios"
import App from "../App.vue"
import router from "../router/index.js"
import { frontendTabData } from "../js-modules/frontendTabData.js"
import { v4 as uuidv4 } from 'uuid';
import { useUserStore } from "../stores/userStore.js"
import { getCsrfToken, refreshCsrfToken } from "../js-modules/csrfToken.js"
import config from 'devextreme/core/config';
import { devextremeLicenseKey } from '@/js-modules/keys/devextreme-license.js';
import "@/stylesheets/main.scss"
import "devextreme/dist/css/dx.common.css"
import "@/stylesheets/dx.fluent.lease-analytics.css"
import "@/stylesheets/devextreme-overrides.scss"
import "devextreme/ui/form";
import "devextreme/ui/text_area";

config({ licenseKey: devextremeLicenseKey });

frontendTabData.tabId =  uuidv4();
  // crypto.randomUUID?.() ??

axios.interceptors.request.use((config) => {
  config.headers['Factz-Tab-Id'] = frontendTabData.tabId

  // Safely get CSRF token - use utility function that handles missing meta tag
  const csrfToken = getCsrfToken()
  if (csrfToken) {
    config.headers['X-CSRF-Token'] = csrfToken
  }

  return config;
})

const pinia = createPinia()

const app = createApp(App)
app.use(router)
app.use(pinia)

// CSRF Token
// Track retry attempts to prevent infinite loops
const csrfRetryAttempted = new Set()

const AUTH_ENDPOINTS = [
  '/api/authentication/login',
  '/api/authentication/logout',
  '/api/authentication/current_user',
  '/api/authentication/register',
  '/api/authentication/reset_password',
  '/api/authentication/forgot_password'
]

const isAuthEndpoint = (url) => AUTH_ENDPOINTS.some(endpoint => url?.includes(endpoint))

const isCsrfError = (response) => {
  if (response?.status !== 422) return false
  const message = response.data?.error || response.data?.message || ''
  return /InvalidAuthenticityToken|CSRF|Can't verify CSRF token/.test(message)
}

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error
    const url = config?.url

    // Handle CSRF token errors - retry once with fresh token
    if (isCsrfError(response)) {
      const retryKey = `${url}-${JSON.stringify(config?.data || '')}`

      if (!csrfRetryAttempted.has(retryKey)) {
        csrfRetryAttempted.add(retryKey)
        setTimeout(() => csrfRetryAttempted.delete(retryKey), 5000)

        try {
          await refreshCsrfToken()

          if (!isAuthEndpoint(url)) {
            config.headers['X-CSRF-Token'] = getCsrfToken()
            return axios.request(config)
          }
        }
        catch (e) {
          console.error('Failed to refresh CSRF token:', e)
        }
      }
    }

    // Handle 401 - logout unless it's an auth endpoint
    if (response?.status === 401 && !isAuthEndpoint(url)) {
      useUserStore().logout()
    }

    return Promise.reject(error)
  }
)

app.mount("#app")
